
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    BarChart3,
    ChevronRight,
    Globe,
    Target,
    Zap,
    TrendingUp,
    TrendingDown,
    LayoutDashboard,
    Search,
    Timer,
    Info,
    ArrowUpRight,
    PieChart,
    Activity,
    Wallet,
    CheckCircle2,
    X,
    TrendingUp as TrendingIcon,
    RefreshCw,
    CircleDollarSign,
    History,
    ShieldCheck
} from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { useETF, ETF } from '@/shared/hooks/useETF';
import { useUserAssets } from '@/shared/hooks/useUserAssets';
import { useUserETF } from '@/shared/hooks/useUserETF';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/shared/hooks/useAuth';
import { useKYC } from '@/shared/hooks/useKYC';
import { useTransactionManager } from '@/shared/hooks/useTransactionManager';
import TradingSuspendedModal from '@/shared/components/TradingSuspendedModal';

const ETFPage: React.FC = () => {
    const navigate = useNavigate();
    const { user, profile } = useAuth();
    const { status: kycStatus, loading: kycLoading } = useKYC(user?.uid);
    const { etfs, loading: etfLoading } = useETF();
    const { assets, updateBalance } = useUserAssets();
    const { positions, recordInvestment, sellETF } = useUserETF();
    const { addTransaction } = useTransactionManager(user?.uid);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedETF, setSelectedETF] = useState<ETF | null>(null);
    const [purchaseStep, setPurchaseStep] = useState<'view' | 'confirm' | 'success'>('view');
    const [purchaseAmount, setPurchaseAmount] = useState<string>('');
    const [submitting, setSubmitting] = useState(false);
    const [showKYCWarning, setShowKYCWarning] = useState(false);
    const [showTradingSuspendedModal, setShowTradingSuspendedModal] = useState(false);

    const filteredEtfs = etfs.filter(e =>
        e.status === 'Active' &&
        (e.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            e.ticker.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const closeModal = () => {
        setSelectedETF(null);
        setPurchaseStep('view');
        setPurchaseAmount('');
    };

    const handlePurchase = async () => {
        // 🚨 거래정지 확인
        if (profile?.tradingSuspended) {
            setShowTradingSuspendedModal(true);
            return;
        }

        // Check KYC level before allowing purchase
        if (!kycStatus || kycStatus.level < 3) {
            setShowKYCWarning(true);
            return;
        }

        if (!selectedETF || !assets || !user) return;
        const amount = Number(purchaseAmount);

        if (isNaN(amount) || amount <= 0) {
            alert('올바른 투자 금액을 입력해주세요.');
            return;
        }

        if (amount > assets.krw) {
            alert('보유 원화 잔액이 부족합니다.');
            return;
        }

        setSubmitting(true);
        try {
            await updateBalance('krw', -amount);
            await recordInvestment(
                selectedETF.id,
                selectedETF.name,
                selectedETF.ticker,
                amount,
                selectedETF.currentPrice
            );
            await addTransaction({
                userId: user.uid,
                type: 'ETF_BUY',
                asset: selectedETF.ticker,
                amount: `-${amount}`,
                price: selectedETF.currentPrice.toString(),
                status: 'COMPLETED'
            } as any);
            setPurchaseStep('success');
        } catch (e: any) {
            alert('투자 처리 중 오류가 발생했습니다: ' + e.message);
        }
        setSubmitting(false);
    };

    const handleSell = async (etfId: string, quantity: number, currentPrice: number, ticker: string) => {
        if (!user) return;
        // 🚨 거래정지 확인
        if (profile?.tradingSuspended) {
            setShowTradingSuspendedModal(true);
            return;
        }
        if (!window.confirm(`정말로 이 ETF 포지션을 전량 환매하시겠습니까?\n현재가 기준 환급 예정액: ₩${(quantity * currentPrice).toLocaleString()}`)) {
            return;
        }
        setSubmitting(true);
        try {
            await sellETF(etfId, quantity, currentPrice);
            await addTransaction({
                userId: user.uid,
                type: 'ETF_SELL',
                asset: ticker,
                amount: `+${(quantity * currentPrice)}`,
                price: currentPrice.toString(),
                status: 'COMPLETED'
            } as any);
            alert('환매 처리가 완료되었습니다. 원화 잔액을 확인해주세요.');
        } catch (e: any) {
            alert('환매 처리 중 오류가 발생했습니다: ' + e.message);
        }
        setSubmitting(false);
    };

    // Stats calculations
    const totalInvested = positions.reduce((sum, p) => sum + p.investedAmount, 0);
    const currentEvaluation = positions.reduce((sum, p) => {
        const etf = etfs.find(e => e.id === p.id);
        const currentPrice = etf?.currentPrice || p.avgPrice;
        return sum + (p.quantity * currentPrice);
    }, 0);
    const totalProfit = currentEvaluation - totalInvested;
    const totalProfitRate = totalInvested > 0 ? (totalProfit / totalInvested) * 100 : 0;

    const PriceText = ({ value, prefix = '₩' }: { value: number, prefix?: string }) => {
        const [flash, setFlash] = useState<'up' | 'down' | null>(null);
        const prevValue = useRef(value);
        useEffect(() => {
            if (value > prevValue.current) {
                setFlash('up');
                setTimeout(() => setFlash(null), 1000);
            } else if (value < prevValue.current) {
                setFlash('down');
                setTimeout(() => setFlash(null), 1000);
            }
            prevValue.current = value;
        }, [value]);
        return (
            <span className={cn(
                "transition-colors duration-500 font-black tabular-nums",
                flash === 'up' && "text-emerald-500 scale-110",
                flash === 'down' && "text-rose-500 scale-110",
                !flash && "text-foreground"
            )}>
                {prefix}{value?.toLocaleString()}
            </span>
        );
    };

    const MiniChart = ({ data, color }: { data: number[], color: string }) => {
        if (!data || data.length < 2) return <div className="h-full flex items-center justify-center text-[10px] text-muted-foreground uppercase">N/A</div>;
        const min = Math.min(...data);
        const max = Math.max(...data);
        const range = max - min || 1;
        const points = data.map((v, i) => `${(i / (data.length - 1)) * 100},${80 - ((v - min) / range) * 60}`).join(' ');
        return (
            <svg className="w-full h-full overflow-visible" viewBox="0 0 100 80">
                <polyline fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" points={points} className="drop-shadow-[0_0_8px_rgba(16,185,129,0.3)]" />
            </svg>
        );
    };

    if (etfLoading || kycLoading) return <div className="min-h-screen flex items-center justify-center text-emerald-500"><RefreshCw className="animate-spin h-10 w-10" /></div>;

    return (
        <div className="min-h-screen pb-24 text-foreground animate-in fade-in duration-1000">
            <div className="max-w-7xl mx-auto px-4 lg:px-8 space-y-12">

                {/* Header */}
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10 pt-8">
                    <div className="space-y-4 max-w-2xl">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 font-black text-[10px] uppercase tracking-widest">
                            <Target className="h-3.5 w-3.5" /> DIGITAL INDEX ETF
                        </div>
                        <h1 className="text-4xl md:text-5xl font-black tracking-tighter leading-[0.9] uppercase">
                            전략적 자산 배분,<br />
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-emerald-600 to-cyan-600">가상자산 ETF 센터</span>
                        </h1>
                    </div>
                    <div className="relative group w-full sm:w-80">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <input
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="w-full h-14 bg-card border border-border rounded-2xl pl-12 pr-4 text-xs font-black outline-none focus:border-emerald-500 shadow-lg"
                            placeholder="상품명 또는 티커 검색..."
                        />
                    </div>
                </div>

                {/* Portfolio Summary */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    <div className="lg:col-span-3 p-10 rounded-[3rem] bg-card border border-border relative overflow-hidden group shadow-2xl">
                        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-12">
                            <div className="space-y-6">
                                <div className="space-y-1">
                                    <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">나의 ETF 포트폴리오 평가</p>
                                    <h2 className="text-4xl md:text-5xl font-black tabular-nums tracking-tighter">₩{currentEvaluation.toLocaleString()}</h2>
                                </div>
                                <div className="flex flex-wrap gap-8">
                                    <div>
                                        <p className="text-[9px] font-black text-muted-foreground uppercase">수익금</p>
                                        <p className={cn("text-lg font-black", totalProfit >= 0 ? "text-emerald-500" : "text-rose-500")}>
                                            ₩{Math.abs(totalProfit).toLocaleString()}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-[9px] font-black text-muted-foreground uppercase">수익률</p>
                                        <p className={cn("text-lg font-black", totalProfitRate >= 0 ? "text-emerald-500" : "text-rose-500")}>
                                            {totalProfitRate >= 0 ? '+' : ''}{totalProfitRate.toFixed(2)}%
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-[9px] font-black text-muted-foreground uppercase">투자 원금</p>
                                        <p className="text-lg font-black opacity-60">₩{totalInvested.toLocaleString()}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="h-24 w-60">
                                <MiniChart data={[totalInvested, totalInvested * 1.05, totalInvested * 0.98, currentEvaluation]} color={totalProfit >= 0 ? '#10b981' : '#f43f5e'} />
                            </div>
                        </div>
                    </div>
                    <div className="p-8 rounded-[3rem] bg-accent/30 border border-border flex flex-col justify-center gap-4 group">
                        <CircleDollarSign className="h-8 w-8 text-emerald-500" />
                        <div>
                            <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">투자 가능 원화</p>
                            <p className="text-2xl font-black">₩{assets?.krw.toLocaleString()}</p>
                        </div>
                    </div>
                    {/* 내역 보기 버튼 추가 */}
                    <div className="lg:col-span-1 p-8 rounded-[3rem] bg-accent/30 border border-border flex flex-col justify-center gap-4 group cursor-pointer hover:bg-accent/50 transition-all" onClick={() => navigate('/history')}>
                        <History className="h-8 w-8 text-blue-500" />
                        <div>
                            <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">나의 투자 내역</p>
                            <p className="text-sm font-black flex items-center gap-1 group-hover:translate-x-1 transition-transform">트랜잭션 보기 <ChevronRight className="h-4 w-4" /></p>
                        </div>
                    </div>
                </div>

                {/* My Positions List */}
                {positions.length > 0 && (
                    <div className="space-y-6">
                        <h3 className="text-xl font-black uppercase tracking-tighter flex items-center gap-2 italic">
                            <Activity className="h-5 w-5 text-emerald-500" /> 보유 중인 ETF 포지션
                        </h3>
                        <div className="grid grid-cols-1 gap-4">
                            {positions.map((pos) => {
                                const etf = etfs.find(e => e.id === pos.id);
                                const currentPrice = etf?.currentPrice || pos.avgPrice;
                                const profitRate = ((currentPrice - pos.avgPrice) / pos.avgPrice) * 100;
                                return (
                                    <div key={pos.id} className="p-8 rounded-[2rem] bg-card border border-border flex flex-col md:flex-row md:items-center justify-between gap-8 shadow-xl transition-all hover:border-emerald-500/20">
                                        <div className="flex items-center gap-6">
                                            <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center text-emerald-500 font-black">{pos.ticker}</div>
                                            <div>
                                                <h4 className="text-xl font-black uppercase">{pos.name}</h4>
                                                <p className="text-[10px] font-bold text-muted-foreground">{pos.ticker} | EVALUATED LIVE</p>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 flex-1 max-w-3xl">
                                            <div><p className="text-[9px] font-black text-muted-foreground uppercase">보유 단위</p><p className="font-black text-lg tabular-nums">{pos.quantity.toFixed(4)}</p></div>
                                            <div><p className="text-[9px] font-black text-muted-foreground uppercase">평단가</p><p className="font-black text-lg tabular-nums">₩{pos.avgPrice.toLocaleString()}</p></div>
                                            <div><p className="text-[9px] font-black text-muted-foreground uppercase">현재가</p><p className="font-black text-lg tabular-nums"><PriceText value={currentPrice} /></p></div>
                                            <div><p className="text-[9px] font-black text-muted-foreground uppercase">수익률</p><p className={cn("font-black text-lg", profitRate >= 0 ? "text-emerald-500" : "text-rose-500")}>{profitRate.toFixed(2)}%</p></div>
                                        </div>
                                        <button onClick={() => handleSell(pos.id, pos.quantity, currentPrice, pos.ticker)} disabled={submitting} className="h-14 px-10 rounded-xl bg-accent hover:bg-emerald-500/10 hover:text-emerald-500 text-[11px] font-black uppercase border border-border shadow-md active:scale-95">
                                            전량 환매
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Market List */}
                <div className="rounded-[3rem] bg-card border border-border overflow-hidden shadow-2xl">
                    <div className="px-10 py-8 bg-accent/30 border-b border-border">
                        <h3 className="font-black flex items-center gap-3 text-lg tracking-tighter uppercase"><LayoutDashboard className="h-5 w-5 text-emerald-500" /> ETF MARKET LIST</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="text-[10px] font-black text-muted-foreground uppercase border-b border-border bg-accent/10">
                                    <th className="px-10 py-6">상품명</th>
                                    <th className="px-10 py-6">현재가</th>
                                    <th className="px-10 py-6">ROI</th>
                                    <th className="px-10 py-6">AUM</th>
                                    <th className="px-10 py-6 text-right">투자</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {filteredEtfs.map((etf) => (
                                    <tr key={etf.id} onClick={() => setSelectedETF(etf)} className="group hover:bg-accent/50 transition-all cursor-pointer">
                                        <td className="px-10 py-8">
                                            <div className="flex items-center gap-5">
                                                <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center group-hover:bg-emerald-500/10 transition-colors"><BarChart3 className="h-5 w-5" /></div>
                                                <div><p className="font-black uppercase">{etf.name}</p><p className="text-[10px] text-muted-foreground">{etf.ticker}</p></div>
                                            </div>
                                        </td>
                                        <td className="px-10 py-8 font-black tabular-nums"><PriceText value={etf.currentPrice} /></td>
                                        <td className="px-10 py-8">
                                            <span className={cn("px-3 py-1 rounded-lg text-xs font-black", etf.roi >= 0 ? "bg-emerald-500/10 text-emerald-600" : "bg-rose-500/10 text-rose-600")}>
                                                {etf.roi}%
                                            </span>
                                        </td>
                                        <td className="px-10 py-8 text-sm font-black text-muted-foreground italic">₩{(etf.aum / 100000000).toFixed(0)}억</td>
                                        <td className="px-10 py-8 text-right">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedETF(etf);
                                                }}
                                                className="h-10 px-6 rounded-xl bg-emerald-600 text-white text-[10px] font-black uppercase shadow-lg hover:scale-105 transition-all"
                                            >
                                                BUY
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Selection Modal */}
            {selectedETF && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
                    <div className="bg-card border border-border rounded-[3rem] w-full max-w-xl overflow-hidden flex flex-col shadow-2xl scale-in-center">
                        <div className="px-10 py-8 border-b border-border flex items-center justify-between bg-accent/30">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-600"><Activity className="h-6 w-6" /></div>
                                <h2 className="text-xl font-black uppercase">{selectedETF.name}</h2>
                            </div>
                            <button onClick={closeModal} className="w-10 h-10 rounded-full hover:bg-accent flex items-center justify-center"><X className="h-6 w-6" /></button>
                        </div>
                        <div className="p-10 space-y-8">
                            {purchaseStep === 'view' && (
                                <>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="p-6 rounded-2xl bg-accent/20 border border-border"><p className="text-[9px] font-black text-muted-foreground uppercase">현재가</p><p className="text-xl font-black tabular-nums"><PriceText value={selectedETF.currentPrice} /></p></div>
                                        <div className="p-6 rounded-2xl bg-accent/20 border border-border"><p className="text-[9px] font-black text-muted-foreground uppercase">수익률</p><p className={cn("text-xl font-black tabular-nums", selectedETF.roi >= 0 ? "text-emerald-500" : "text-rose-500")}>{selectedETF.roi}%</p></div>
                                    </div>
                                    <div className="space-y-4">
                                        <h4 className="text-[10px] font-black uppercase tracking-widest flex items-center gap-2"><PieChart className="h-4 w-4 text-emerald-500" /> ASSET ALLOCATION</h4>
                                        <div className="space-y-2">
                                            {selectedETF.holdings?.map((h, i) => (
                                                <div key={i} className="flex items-center justify-between p-4 rounded-xl bg-accent/10 border border-border/50">
                                                    <span className="text-xs font-black uppercase">{h.asset}</span>
                                                    <span className="text-xs font-black text-emerald-500 tabular-nums">{h.weight}%</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="flex gap-4 pt-4">
                                        <button onClick={closeModal} className="flex-1 h-14 rounded-xl bg-card border border-border text-xs font-black uppercase">CANCEL</button>
                                        <button onClick={() => setPurchaseStep('confirm')} className="flex-1 h-14 rounded-xl bg-emerald-600 text-white text-xs font-black uppercase shadow-xl shadow-emerald-600/20">INVEST NOW</button>
                                    </div>
                                </>
                            )}
                            {purchaseStep === 'confirm' && (
                                <div className="space-y-6">
                                    <div className="text-center"><h3 className="text-xl font-black uppercase">INVESTMENT CONFIRM</h3><p className="text-xs font-bold text-muted-foreground">원화(KRW)로 투자를 확정합니다.</p></div>
                                    <div className="space-y-4">
                                        <div><p className="text-[9px] font-black text-muted-foreground uppercase pl-1">투자 금액 (₩)</p><input type="number" value={purchaseAmount} onChange={e => setPurchaseAmount(e.target.value)} className="w-full h-14 bg-accent/20 border border-border rounded-xl px-4 font-black" placeholder="0" /></div>
                                        <p className="text-[10px] text-center text-muted-foreground italic font-bold">운용 수수료 연 0.85% 차감 후 정산됩니다.</p>
                                    </div>
                                    <div className="flex gap-4">
                                        <button onClick={() => setPurchaseStep('view')} className="flex-1 h-14 rounded-xl bg-card border border-border text-xs font-black uppercase">BACK</button>
                                        <button onClick={handlePurchase} disabled={submitting} className="flex-1 h-14 rounded-xl bg-emerald-600 text-white text-xs font-black uppercase shadow-xl shadow-emerald-600/20">{submitting ? 'PROCESSING...' : 'CONFIRM BUY'}</button>
                                    </div>
                                </div>
                            )}
                            {purchaseStep === 'success' && (
                                <div className="text-center space-y-6 py-4">
                                    <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto"><CheckCircle2 className="h-10 w-10 text-emerald-600" /></div>
                                    <h3 className="text-2xl font-black uppercase tracking-tighter italic">SUCCESSFULLY INVESTED</h3>
                                    <button onClick={closeModal} className="w-full h-14 rounded-xl bg-emerald-600 text-white text-xs font-black uppercase shadow-xl">DONE</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* KYC Warning Modal */}
            {showKYCWarning && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
                    <div className="bg-card border border-border rounded-[3rem] w-full max-w-md p-10 space-y-8 shadow-2xl scale-in-center">
                        <div className="w-20 h-20 mx-auto rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                            <ShieldCheck className="h-10 w-10 text-amber-500" />
                        </div>
                        <div className="space-y-4 text-center">
                            <h2 className="text-2xl font-black text-foreground uppercase tracking-tight">KYC 레벨 3 인증 필요</h2>
                            <p className="text-sm font-bold text-muted-foreground leading-relaxed">
                                가상자산 ETF 서비스는 KYC 레벨 3 인증이 완료된 사용자만 이용하실 수 있습니다.<br />
                                주소 인증과 사업자 인증을 완료해주세요.
                            </p>
                            <p className="text-xs font-bold text-muted-foreground">현재 KYC 레벨: {kycStatus?.level || 0} / 3</p>
                        </div>
                        <div className="flex gap-4">
                            <button
                                onClick={() => setShowKYCWarning(false)}
                                className="flex-1 h-14 rounded-xl bg-card border border-border text-xs font-black uppercase"
                            >
                                닫기
                            </button>
                            <button
                                onClick={() => navigate('/verification')}
                                className="flex-1 h-14 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-black text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2"
                            >
                                <ShieldCheck className="h-5 w-5" />
                                인증하기
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 거래정지 모달 */}
            {showTradingSuspendedModal && (
                <TradingSuspendedModal onClose={() => setShowTradingSuspendedModal(false)} />
            )}
        </div>
    );
};

export default ETFPage;
